import axiosInst from "api/config/axiosConfig";
import { disconnectDeviceVpn, updateVpnDeviceStatus } from "api/DeviceHelpers";

export const VPN_CONNECTION_TIMEOUT_MS = 5 * 60 * 1000;
const REQUEST_TIMEOUT_MS = 15000;
const POLL_INTERVAL_MS = 3000;

export interface MissionControlStatus {
  serialNumber: string;
  vpnStatus: string;
  mcUrl: string;
  diagnostics?: {
    reasonCode?: string;
    missionControlUrl?: string;
    lastHandshakeAt?: number;
    certificate?: {
      available: boolean;
      reason?: string;
      reportedAt?: number;
      checkedAt?: number;
      revocationStatus?: "good" | "revoked" | "unknown";
      notAfter?: string;
    };
  };
}

export function missionControlAddress(status?: MissionControlStatus): URL | null {
  const value = status?.mcUrl || status?.diagnostics?.missionControlUrl;
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === "https:" && !url.username && !url.password ? url : null;
  } catch {
    return null;
  }
}

export function vpnFailureMessage(status?: MissionControlStatus): string {
  switch (status?.diagnostics?.reasonCode) {
    case "vpn_service_unavailable":
      return "The portal could not check the VPN service. Please retry or contact Airgain support.";
    case "vpn_public_key_missing":
      return "The device has not reported its VPN public key. Contact Airgain support if retrying does not help.";
    case "vpn_configuration_timed_out":
      return "The device did not complete VPN setup in time. Check that it is online, then retry.";
    case "vpn_request_time_invalid":
      return "The portal could not read the VPN setup state. Please contact Airgain support.";
    default:
      return status?.vpnStatus === "Connection Failed"
        ? "VPN setup failed. Please retry or contact Airgain support."
        : "The VPN connection was not established. Check that the device is online, then retry.";
  }
}

export function missionControlErrorMessage(error: any): string {
  if (error?.code === "VPN_TIMEOUT")
    return "VPN connection timed out after 5 minutes. Check that the device is online, then retry.";
  if (error?.code === "ECONNABORTED" || error?.code === "ETIMEDOUT") {
    return "The portal's VPN request timed out. Please retry or contact Airgain support.";
  }
  if (error?.code === "ERR_NETWORK") {
    return "The browser could not reach the portal's VPN API. Check your network connection and retry.";
  }
  // The shared interceptor rejects HTTP errors with the response itself.
  const data = error?.response?.data || error?.data;
  const detail = data?.error || data?.message || error?.message;
  return typeof detail === "string" && detail
    ? detail
    : "The portal could not prepare Mission Control. Please retry or contact Airgain support.";
}

export function getMissionControlStatus(serialNumber: string, signal?: AbortSignal): Promise<MissionControlStatus> {
  return axiosInst
    .get<MissionControlStatus>("/beta/vpnUIHook", {
      params: { serialNumber, diagnostics: "true" },
      timeout: REQUEST_TIMEOUT_MS,
      signal,
    })
    .then((response) => response.data);
}

function connectionError(message: string, code: string): Error & { code: string } {
  return Object.assign(new Error(message), { code });
}

function waitForPoll(signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout>;
    const cancelled = () => {
      clearTimeout(timer);
      reject(connectionError("VPN connection cancelled", "ERR_CANCELED"));
    };
    timer = setTimeout(() => {
      signal.removeEventListener("abort", cancelled);
      resolve();
    }, POLL_INTERVAL_MS);
    signal.addEventListener("abort", cancelled, { once: true });
    if (signal.aborted) cancelled();
  });
}

interface ConnectOptions {
  reconnect?: boolean;
  signal?: AbortSignal;
  onStatus?: (status: MissionControlStatus) => void;
}

// One deadline covers status checks, setup, disconnect, and polling. Cancelling
// stops browser requests; it does not send a new device command.
export async function connectMissionControl(
  serialNumber: string,
  options: ConnectOptions = {},
): Promise<MissionControlStatus> {
  const controller = new AbortController();
  let rejectInterrupted: (error: Error) => void = () => {};
  const interrupted = new Promise<never>((_resolve, reject) => {
    rejectInterrupted = reject;
  });
  const stop = (code: string, message: string) => {
    rejectInterrupted(connectionError(message, code));
    controller.abort();
  };
  const cancel = () => stop("ERR_CANCELED", "VPN connection cancelled");
  options.signal?.addEventListener("abort", cancel, { once: true });
  const deadline = setTimeout(() => stop("VPN_TIMEOUT", "VPN connection timed out"), VPN_CONNECTION_TIMEOUT_MS);
  const requestOptions = { timeout: REQUEST_TIMEOUT_MS, signal: controller.signal };

  const check = (status: MissionControlStatus, terminal: boolean): boolean => {
    if (controller.signal.aborted) throw connectionError("VPN connection cancelled", "ERR_CANCELED");
    if (!status || typeof status.vpnStatus !== "string") {
      throw connectionError(
        "The VPN service returned an unreadable status. Please retry or contact Airgain support.",
        "VPN_INVALID_RESPONSE",
      );
    }
    options.onStatus?.(status);
    if (
      status?.diagnostics?.reasonCode === "vpn_service_unavailable" ||
      (terminal && ["Connection Failed", "Error"].includes(status?.vpnStatus))
    ) {
      throw connectionError(vpnFailureMessage(status), "VPN_FAILED");
    }
    return status?.vpnStatus === "Connected" && !!missionControlAddress(status);
  };

  const connect = async () => {
    if (options.signal?.aborted) cancel();
    if (controller.signal.aborted) throw connectionError("VPN connection cancelled", "ERR_CANCELED");
    let status: MissionControlStatus | undefined;
    if (options.reconnect) {
      await disconnectDeviceVpn(serialNumber, requestOptions);
    } else {
      status = await getMissionControlStatus(serialNumber, controller.signal);
      if (check(status, false)) return status;
    }
    if (controller.signal.aborted) throw connectionError("VPN connection cancelled", "ERR_CANCELED");
    if (!status || !["In Progress", "VPN initialized", "Connected"].includes(status.vpnStatus)) {
      const response = await updateVpnDeviceStatus(serialNumber, requestOptions);
      status = response.data;
      check(status as MissionControlStatus, true);
      // Read the saved address and diagnostics after setup. The initialization
      // response can have been built from the device record before assignment.
      if (status?.vpnStatus === "Connected") {
        status = await getMissionControlStatus(serialNumber, controller.signal);
        if (check(status, true)) return status;
      }
    }
    while (!controller.signal.aborted) {
      // eslint-disable-next-line no-await-in-loop -- poll sequentially without overlapping checks
      await waitForPoll(controller.signal);
      // eslint-disable-next-line no-await-in-loop
      status = await getMissionControlStatus(serialNumber, controller.signal);
      if (check(status, true)) return status;
      if (status.vpnStatus === "Connected") {
        throw connectionError(
          "The VPN is connected, but the Mission Control address is unavailable. Please contact Airgain support.",
          "VPN_ADDRESS_MISSING",
        );
      }
    }
    throw connectionError("VPN connection cancelled", "ERR_CANCELED");
  };
  try {
    return await Promise.race([connect(), interrupted]);
  } finally {
    clearTimeout(deadline);
    options.signal?.removeEventListener("abort", cancel);
    controller.abort();
  }
}
